import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  MenuItem,
  Typography,
  InputAdornment,
  Chip,
  Avatar,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Search as SearchIcon, 
  Clear as ClearIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Transgender as OtherIcon,
  LocationOn as LocationIcon,
  InsertInvitation as InsuranceIcon,
} from '@mui/icons-material';
import { Patient, Location, Insurance } from '../types/Patient';
import apiService from '../services/api';

const sexOptions = ['Male', 'Female', 'Other'];

const Patients: React.FC = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [formData, setFormData] = useState({
    patientId: '',
    name: '',
    dob: '',
    sex: '',
    mrn: '',
    locationId: null as string | null,
  });
  const [locations, setLocations] = useState<Location[]>([]);
  const [patientInsurances, setPatientInsurances] = useState<Insurance[]>([]);
  const [openInsuranceDialog, setOpenInsuranceDialog] = useState(false);
  const [editingInsurance, setEditingInsurance] = useState<Insurance | null>(null);
  const [insuranceFormData, setInsuranceFormData] = useState({
    insuranceCompany: '',
    policyNumber: '',
    groupNumber: '',
    subscriberId: '',
    subscriberName: '',
    subscriberRelationship: '',
    effectiveDate: '',
    expirationDate: '',
    isPrimary: false,
    isActive: true,
    copay: '',
    deductible: '',
  });

  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getPatients(page + 1, rowsPerPage, searchTerm || undefined);
      setPatients(response.patients || []);
      setTotalCount(response.totalCount || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch patients');
      console.error('Error fetching patients:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm]);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await apiService.getAllLocations();
      // Filter out HQ locations for patient creation
      const labSiteLocations = response.filter((location: any) => location.location_type !== 'HQ');
      console.log('Fetched locations:', labSiteLocations);
      setLocations(labSiteLocations);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  }, []);

  const fetchPatientInsurances = useCallback(async (patientId: string) => {
    try {
      const response = await apiService.getPatientInsurances(patientId);
      setPatientInsurances(response.insurances || []);
    } catch (err) {
      console.error('Error fetching patient insurances:', err);
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPatients();
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [fetchPatients]);

  // Fetch locations on component mount
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPatient) {
        await apiService.updatePatient(editingPatient.id, formData);
        setOpenDialog(false);
        setEditingPatient(null);
        resetForm();
        fetchPatients();
      } else {
        // Create new patient and switch to edit mode to allow adding insurance
        const newPatient = await apiService.createPatient(formData);
        
        // Switch to edit mode with the newly created patient
        setEditingPatient(newPatient);
        
        // Fetch patient insurances (will be empty for new patient)
        fetchPatientInsurances(newPatient.id);
        
        // Refresh the patients list but keep dialog open
        fetchPatients();
        
        // Show success message
        console.log('Patient created successfully. You can now add insurance records.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save patient');
      console.error('Error saving patient:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this patient?')) {
      try {
        await apiService.deletePatient(id);
        fetchPatients();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete patient');
        console.error('Error deleting patient:', err);
      }
    }
  };

  const handleEdit = (patient: Patient) => {
    console.log('Editing patient:', patient);
    console.log('Patient locationId:', patient.locationId, 'Type:', typeof patient.locationId);
    setEditingPatient(patient);
    setFormData({
      patientId: patient.patientId || '',
      name: patient.name || '',
      dob: patient.dob || '',
      sex: patient.sex || '',
      mrn: patient.mrn || '',
      locationId: patient.locationId ? String(patient.locationId) : null,
    });
    if (patient.id) {
      fetchPatientInsurances(patient.id);
    }
    setOpenDialog(true);
  };

  const resetForm = () => {
    setFormData({
      patientId: '',
      name: '',
      dob: '',
      sex: '',
      mrn: '',
      locationId: null as string | null,
    });
    setPatientInsurances([]);
  };

  const resetInsuranceForm = () => {
    setInsuranceFormData({
      insuranceCompany: '',
      policyNumber: '',
      groupNumber: '',
      subscriberId: '',
      subscriberName: '',
      subscriberRelationship: '',
      effectiveDate: '',
      expirationDate: '',
      isPrimary: false,
      isActive: true,
      copay: '',
      deductible: '',
    });
  };

  const handleOpenDialog = () => {
    setEditingPatient(null);
    resetForm();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPatient(null);
    resetForm();
  };

  const handleOpenInsuranceDialog = (insurance?: Insurance) => {
    if (insurance) {
      setEditingInsurance(insurance);
      setInsuranceFormData({
        insuranceCompany: insurance.insuranceCompany || '',
        policyNumber: insurance.policyNumber || '',
        groupNumber: insurance.groupNumber || '',
        subscriberId: insurance.subscriberId || '',
        subscriberName: insurance.subscriberName || '',
        subscriberRelationship: insurance.subscriberRelationship || '',
        effectiveDate: insurance.effectiveDate || '',
        expirationDate: insurance.expirationDate || '',
        isPrimary: insurance.isPrimary || false,
        isActive: insurance.isActive !== false,
        copay: insurance.copay?.toString() || '',
        deductible: insurance.deductible?.toString() || '',
      });
    } else {
      setEditingInsurance(null);
      resetInsuranceForm();
    }
    setOpenInsuranceDialog(true);
  };

  const handleCloseInsuranceDialog = () => {
    setOpenInsuranceDialog(false);
    setEditingInsurance(null);
    resetInsuranceForm();
  };

  const handleInsuranceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPatient) return;
    
    try {
      const insuranceData = {
        ...insuranceFormData,
        patientId: editingPatient.id,
        copay: insuranceFormData.copay ? parseFloat(insuranceFormData.copay) : undefined,
        deductible: insuranceFormData.deductible ? parseFloat(insuranceFormData.deductible) : undefined,
        // Handle empty date fields properly - convert empty strings to undefined
        effectiveDate: insuranceFormData.effectiveDate && insuranceFormData.effectiveDate.trim() !== '' ? insuranceFormData.effectiveDate : undefined,
        expirationDate: insuranceFormData.expirationDate && insuranceFormData.expirationDate.trim() !== '' ? insuranceFormData.expirationDate : undefined,
      };

      if (editingInsurance) {
        await apiService.updateInsurance(editingInsurance.id, insuranceData);
      } else {
        await apiService.createInsurance(insuranceData);
      }
      
      handleCloseInsuranceDialog();
      fetchPatientInsurances(editingPatient.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save insurance');
      console.error('Error saving insurance:', err);
    }
  };

  const handleDeleteInsurance = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this insurance record?')) {
      try {
        await apiService.deleteInsurance(id);
        if (editingPatient) {
          fetchPatientInsurances(editingPatient.id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete insurance');
        console.error('Error deleting insurance:', err);
      }
    }
  };

  const handleSetPrimaryInsurance = async (id: string) => {
    if (!editingPatient) return;
    
    try {
      await apiService.setInsuranceAsPrimary(id, editingPatient.id);
      fetchPatientInsurances(editingPatient.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set primary insurance');
      console.error('Error setting primary insurance:', err);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getSexIcon = (sex?: string) => {
    switch (sex?.toLowerCase()) {
      case 'male':
        return <MaleIcon sx={{ fontSize: 16, color: '#1976d2' }} />;
      case 'female':
        return <FemaleIcon sx={{ fontSize: 16, color: '#e91e63' }} />;
      default:
        return <OtherIcon sx={{ fontSize: 16, color: '#9c27b0' }} />;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e' }}>
            Patients
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage patient records and information
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          sx={{
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            boxShadow: '0 3px 5px 2px rgba(25, 118, 210, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
            }
          }}
        >
          Add Patient
        </Button>
      </Box>

      {/* Search Box */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by patient name, patient ID, or MRN..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#1976d2',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#1976d2',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                    sx={{ color: '#666' }}
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Patients Grid */}
      <Card sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Patient ID
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Name
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Date of Birth
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Sex
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  MRN
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Location
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #1976d2' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        Loading patients...
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please wait while we fetch the data
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : patients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <PersonIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        {searchTerm ? 'No patients found' : 'No patients available'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {searchTerm 
                          ? 'Try adjusting your search terms or clear the search to see all patients.'
                          : 'Get started by adding your first patient record.'
                        }
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                patients.map((patient, index) => (
                  <TableRow 
                    key={patient.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: '#f8f9fa',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease-in-out'
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: '#fafafa'
                      }
                    }}
                  >
                    <TableCell>
                      <Chip 
                        label={patient.patientId} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar 
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            bgcolor: '#1976d2',
                            fontSize: '0.875rem'
                          }}
                        >
                          {patient.name?.charAt(0)?.toUpperCase() || 'P'}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {patient.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarIcon sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="body2">
                          {formatDate(patient.dob)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getSexIcon(patient.sex)}
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {patient.sex || 'Not specified'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {patient.mrn || 'Not assigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationIcon sx={{ fontSize: 16, color: '#666' }} />
                        <Typography variant="body2">
                          {patient.location?.labName || 'Not assigned'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton 
                          onClick={() => handleEdit(patient)}
                          size="small"
                          sx={{ 
                            color: '#1976d2',
                            '&:hover': { 
                              backgroundColor: 'rgba(25, 118, 210, 0.1)',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDelete(patient.id)}
                          size="small"
                          sx={{ 
                            color: '#d32f2f',
                            '&:hover': { 
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box sx={{ borderTop: '1px solid #e0e0e0' }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontWeight: 500,
                color: '#666'
              }
            }}
          />
        </Box>
      </Card>

      {/* Patient Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editingPatient ? 'Edit Patient' : 'Add New Patient'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Patient ID"
                  value={formData.patientId}
                  onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Sex"
                  value={formData.sex}
                  onChange={(e) => setFormData({ ...formData, sex: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                >
                  {sexOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="MRN"
                  value={formData.mrn}
                  onChange={(e) => setFormData({ ...formData, mrn: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Location"
                  value={formData.locationId || ''}
                  onChange={(e) => {
                    console.log('Location selected:', e.target.value);
                    setFormData({ ...formData, locationId: e.target.value || null });
                  }}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="">
                    <em>No location assigned</em>
                  </MenuItem>
                  {locations.map((location) => (
                    <MenuItem key={location.id} value={String(location.id)}>
                      {location.labName} ({location.labCode})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InsuranceIcon />
                    Insurance Records
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenInsuranceDialog()}
                    disabled={!editingPatient}
                  >
                    Add Insurance
                  </Button>
                </Box>
                  
                  {patientInsurances.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      {!editingPatient 
                        ? 'Save the patient first, then you can add insurance records' 
                        : 'No insurance records found'
                      }
                    </Typography>
                  ) : (
                    <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {patientInsurances.map((insurance) => (
                        <Card key={insurance.id} sx={{ mb: 1, p: 1 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {insurance.insuranceCompany}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Policy: {insurance.policyNumber}
                                {insurance.isPrimary && (
                                  <Chip label="Primary" size="small" color="primary" sx={{ ml: 1 }} />
                                )}
                              </Typography>
                            </Box>
                            <Box>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenInsuranceDialog(insurance)}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteInsurance(insurance.id)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Card>
                      ))}
                    </Box>
                  )}
                </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button 
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                }
              }}
            >
              {editingPatient ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Insurance Dialog */}
      <Dialog open={openInsuranceDialog} onClose={handleCloseInsuranceDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
          color: 'white',
          fontWeight: 600
        }}>
          {editingInsurance ? 'Edit Insurance' : 'Add New Insurance'}
        </DialogTitle>
        <form onSubmit={handleInsuranceSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Insurance Company"
                  value={insuranceFormData.insuranceCompany}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, insuranceCompany: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Policy Number"
                  value={insuranceFormData.policyNumber}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, policyNumber: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Group Number"
                  value={insuranceFormData.groupNumber}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, groupNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Subscriber ID"
                  value={insuranceFormData.subscriberId}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, subscriberId: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Subscriber Name"
                  value={insuranceFormData.subscriberName}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, subscriberName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Relationship to Patient"
                  value={insuranceFormData.subscriberRelationship}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, subscriberRelationship: e.target.value })}
                >
                  <MenuItem value="Self">Self</MenuItem>
                  <MenuItem value="Spouse">Spouse</MenuItem>
                  <MenuItem value="Child">Child</MenuItem>
                  <MenuItem value="Parent">Parent</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Effective Date"
                  type="date"
                  value={insuranceFormData.effectiveDate}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, effectiveDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expiration Date"
                  type="date"
                  value={insuranceFormData.expirationDate}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, expirationDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Copay"
                  type="number"
                  value={insuranceFormData.copay}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, copay: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Deductible"
                  type="number"
                  value={insuranceFormData.deductible}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, deductible: e.target.value })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Primary Insurance"
                  value={insuranceFormData.isPrimary ? 'true' : 'false'}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, isPrimary: e.target.value === 'true' })}
                >
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Active"
                  value={insuranceFormData.isActive ? 'true' : 'false'}
                  onChange={(e) => setInsuranceFormData({ ...insuranceFormData, isActive: e.target.value === 'true' })}
                >
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button 
              onClick={handleCloseInsuranceDialog}
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                }
              }}
            >
              {editingInsurance ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Patients; 